import React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { change, formValueSelector } from 'redux-form'
import { useNotifier } from '@cbgms/base/notifier'
import { connect } from 'react-redux'
import { WithTranslation, withTranslation } from 'react-i18next'
import { Alert, AlertTitle, Button, Grid, Stack, Typography } from '@mui/material'
import Dropdown from '@cbgms/components/form/Material/Dropdown'
import Input from '@cbgms/components/form/Material/Input'
import DeleteIcon from '@mui/icons-material/Delete'
import UserIcon from '@mui/icons-material/Person'
import MailIcon from '@mui/icons-material/Mail'
import { EmailOptions, UserStatus } from '@cbgms/api/modules/enums'
import { compose } from '@cbgms/base/utils/compose'
import { enumToLanguageOptions, supportedLanguages } from '@cbgms/base/translate/locales'
import { isRequired, isValidEmail } from '@cbgms/base/utils/validations'
import { IRootReducerState } from 'state/reducers'
import { deleteUser, disableUser, enableUser, inviteShopEmployee, loadEmployeeStatus } from '../../state/actions'
import UserPasswordDialog from './UserPasswordDialog'
import { Loader } from '@carsys/ui'

interface IOwnProps {
  form: string
  onUserDeletion?: () => Promise<void>
}

interface IMapStateToProps {
  isOther: boolean
  EmailAddressWork: string
  EmailAddressOther: string
  selectedLocale: string
  linkedEmail: string
  status: UserStatus
  isEnabled: boolean
}

interface IMapDispatchToProps {
  change: typeof change
  disableUser: typeof disableUser
  enableUser: typeof enableUser
  deleteUser: typeof deleteUser
  inviteShopEmployee: typeof inviteShopEmployee
  loadEmployeeStatus: typeof loadEmployeeStatus
}

export interface UserAccountProps
  extends RouteComponentProps<{ employeeUUID: string; companyUUID: string; shopID: string }>,
    IOwnProps,
    IMapStateToProps,
    IMapDispatchToProps,
    WithTranslation {}

const mapStateToProps = (state: IRootReducerState, { form }: any): IMapStateToProps => {
  const selector = formValueSelector(form)
  const selectedOption = selector(state, 'EmailAddressSelected')
  const selectedLocale = selector(state, 'Locale')

  const EmailAddressWork = selector(state, 'EmailAddressWork')

  // The selected email address for the invite
  const EmailAddressOther = selector(state, 'EmailAddressOther')

  return {
    isOther: selectedOption === EmailOptions.Other,
    EmailAddressWork,
    EmailAddressOther,
    selectedLocale,
    status: state.employee.status.Status,
    linkedEmail: state.employee.status.EmailAddress,
    isEnabled: state.employee.status.Enabled
  }
}

const enhance = compose<UserAccountProps, IOwnProps>(
  withTranslation(),
  withRouter,
  connect(mapStateToProps, { change, deleteUser, disableUser, enableUser, inviteShopEmployee, loadEmployeeStatus })
)

export const UserAccount: React.FC<UserAccountProps> = props => {
  const notifier = useNotifier()
  const [inviting, setInviting] = React.useState(false)
  const [toggling, setToggling] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = React.useState(false)
  const openResetModal = React.useCallback(() => setIsResetModalOpen(true), [setIsResetModalOpen])
  const closeResetModal = React.useCallback(() => setIsResetModalOpen(false), [setIsResetModalOpen])

  const handleEmailAddressChange = (value?: EmailOptions) => {
    const { change, form } = props
    let email = ''

    switch (value) {
      case EmailOptions.Work:
        email = props.EmailAddressWork
        break
      default:
        email = ''
    }

    change(form, 'EmailAddressOther', email)
  }

  const handleEmployeeInvite = () => {
    const { EmailAddressOther, t } = props

    if (!EmailAddressOther) {
      notifier.warning(t('setting_employee.no_email_address'))

      return
    }

    setInviting(true)
    inviteEmployee()
  }

  const inviteEmployee = async () => {
    const {
      EmailAddressOther,
      inviteShopEmployee,
      loadEmployeeStatus,
      match,
      selectedLocale,
      match: {
        params: { companyUUID, shopID }
      },
      t
    } = props

    try {
      await inviteShopEmployee(match.params.employeeUUID, {
        EmailAddress: EmailAddressOther,
        Locale: selectedLocale,
        CompanyUUID: companyUUID,
        CurrentShop: shopID
      })

      await loadEmployeeStatus(match.params.employeeUUID)

      notifier.success(t('setting_employee.invite_sent_success', { email: EmailAddressOther }))
    } catch {
      // ignore error
    } finally {
      setInviting(false)
    }
  }

  const handleDisableUser = async () => {
    const { match, disableUser, enableUser, isEnabled, loadEmployeeStatus, t } = props

    setToggling(true)
    try {
      if (isEnabled) {
        await disableUser(match.params.employeeUUID)
        notifier.success(t('setting_employee.user_disabled_success'))
      } else {
        await enableUser(match.params.employeeUUID)
        notifier.success(t('setting_employee.user_enabled_success'))
      }
      await loadEmployeeStatus(match.params.employeeUUID)
    } finally {
      setToggling(false)
    }
  }

  const handleRemoveUser = async () => {
    const { deleteUser, match, t, onUserDeletion } = props
    setDeleting(true)
    try {
      await deleteUser(match.params.employeeUUID)
      notifier.success(t('setting_employee.user_delete_success'))

      await onUserDeletion?.()
    } finally {
      setDeleting(false)
    }
  }

  const { isEnabled, isOther, linkedEmail, status, t } = props
  const localeOptions = enumToLanguageOptions(supportedLanguages)

  const options = [
    { text: t('setting_employee.email_address', { context: EmailOptions.Work }), value: EmailOptions.Work, key: EmailOptions.Work },
    { text: t('setting_employee.email_address', { context: EmailOptions.Other }), value: EmailOptions.Other, key: EmailOptions.Other }
  ]

  return (
    <Grid container direction='row' spacing={1}>
      {status === UserStatus.Unknown && (
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Grid item>
              <Dropdown
                fullWidth
                name='Locale'
                label={t('setting_employee.select_language')}
                required
                validate={[isRequired]}
                options={localeOptions}
              />
            </Grid>
            <Grid item>
              <Dropdown
                fullWidth
                label={t('setting_employee.email_address')}
                name='EmailAddressSelected'
                onChange={e => handleEmailAddressChange(e?.target.value as any as EmailOptions)}
                options={options}
              />
            </Grid>
            <Grid item>
              <Input
                fullWidth
                disabled={!isOther}
                label={t('setting_employee.email_address_selected')}
                name='EmailAddressOther'
                validate={isValidEmail}
                inputProps={{
                  sx: {
                    border: 'none !important'
                  }
                }}
              />
            </Grid>
            <Grid item>
              <Button color='secondary' variant='outlined' disabled={inviting} startIcon={<MailIcon />} onClick={handleEmployeeInvite}>
                {inviting && <Loader loading size={20} />}
                {t('setting_employee.send_invitation_email')}
              </Button>
            </Grid>
          </Stack>
        </Grid>
      )}
      {status === UserStatus.ForceChangePassword && (
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Grid item>
              <Typography variant='subtitle2'>
                {t('setting_employee.linked_user')} {linkedEmail}
              </Typography>
            </Grid>
            <Grid item>
              <Button variant='outlined' onClick={openResetModal}>
                {t('setting_employee.start_reset')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color={isEnabled ? 'secondary' : 'primary'}
                variant='outlined'
                disabled={toggling}
                startIcon={<UserIcon />}
                onClick={handleDisableUser}
              >
                {toggling && <Loader loading size={20} />}
                {isEnabled ? t('setting_employee.disable_user') : t('setting_employee.enable_user')}
              </Button>
            </Grid>
            {!isEnabled && (
              <Grid item>
                <Button color='secondary' variant='outlined' disabled={deleting} startIcon={<DeleteIcon />} onClick={handleRemoveUser}>
                  {deleting && <Loader loading size={20} />}
                  {t('setting_employee.delete_user')}
                </Button>
              </Grid>
            )}

            <Grid item xs>
              <Alert severity='info'>
                <AlertTitle>
                  {t('setting_employee.invitation_sent_part_one')} - <strong>{linkedEmail}</strong>
                </AlertTitle>
                {t('setting_employee.invitation_sent_part_two')}
              </Alert>
            </Grid>
          </Stack>
        </Grid>
      )}
      {(status === UserStatus.Confirmed || status === UserStatus.ResetRequired) && (
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Grid item>
              <Typography variant='subtitle2'>
                {t('setting_employee.linked_user')} {linkedEmail}
              </Typography>
            </Grid>
            <Grid item>
              <Button variant='outlined' onClick={openResetModal}>
                {t('setting_employee.start_reset')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='outlined'
                color={isEnabled ? 'secondary' : 'primary'}
                disabled={toggling}
                startIcon={<UserIcon />}
                onClick={handleDisableUser}
              >
                {toggling && <Loader loading size={20} />}
                {isEnabled ? t('setting_employee.disable_user') : t('setting_employee.enable_user')}
              </Button>
            </Grid>
            {!isEnabled && (
              <Grid item>
                <Button variant='outlined' color='secondary' disabled={deleting} startIcon={<DeleteIcon />} onClick={handleRemoveUser}>
                  {deleting && <Loader loading size={20} />}
                  {t('setting_employee.delete_user')}
                </Button>
              </Grid>
            )}
          </Stack>
        </Grid>
      )}

      <UserPasswordDialog
        open={isResetModalOpen}
        onClose={closeResetModal}
        enablePasswordReset={status !== UserStatus.ForceChangePassword}
        linkedEmail={linkedEmail}
      />
    </Grid>
  )
}

export default enhance(UserAccount)
